import React, { useState, useEffect, useRef } from 'react';
import "../css/ExplorePage.css";
import { useNavigate } from 'react-router-dom';
import PostDetail from '../components/PostDetail';
import AdVideo from '../AdMedia/AdVideo.mp4';
import AdThumbnail from '../AdMedia/AdThumbnail.jpg';

export default function ExplorePage() {
    const [posts, setPosts] = useState([]);
    const [aboutFilter, setAboutFilter] = useState('');
    const [interestFilter, setInterestFilter] = useState(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user') || 'null');
            const val = (u && u.interests) ? u.interests : '';
            return Array.isArray(val) ? val.join(',') : val;
        } catch (e) { return ''; }
    });
    const [showDetail, setShowDetail] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [mediaIndices, setMediaIndices] = useState({});
    const limitRef = useRef(8);
    const skipRef = useRef(0);
    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const [showFilterRequiredMessage, setShowFilterRequiredMessage] = useState(false);
    const [showFilterBar, setShowFilterBar] = useState(true);
    const [adOpen, setAdOpen] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();


    

    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    const fetchPosts = (offset = 0) => {
        // allow reset fetch even if a previous fetch is running when offset===0
        if (offset !== 0 && (loadingRef.current || !hasMoreRef.current)) return;
        const myReqId = ++requestIdRef.current;
        loadingRef.current = true;
        setLoading(true);
        // require at least one filter
        if (!aboutFilter && !interestFilter) {
            setPosts([]);
            hasMoreRef.current = false;
            setHasMore(false);
            setLoading(false);
            loadingRef.current = false;
            setShowFilterRequiredMessage(true);
            return;
        }
        setShowFilterRequiredMessage(false);
        let url = `/allposts?limit=${limitRef.current}&skip=${offset}`;
        if (aboutFilter) url += `&aboutPostQuery=${encodeURIComponent(aboutFilter)}`;
        if (interestFilter) url += `&interestQuery=${encodeURIComponent(interestFilter)}`;
        fetch(url, {
            headers: { Authorization: "Bearer " + localStorage.getItem("jwt") }
        })
        .then(res => {
            if (res.status === 401) {
                localStorage.removeItem('jwt');
                localStorage.removeItem('user');
                navigate('/signup');
                return null;
            }
            return res.json();
        })
        .then(data => {
            if (!data) return;
            if (myReqId !== requestIdRef.current) { loadingRef.current = false; setLoading(false); return; }
            if (Array.isArray(data) && data.length) {
                setPosts(prev => {
                    const existingIds = new Set(prev.map(p=>p._id));
                    const merged = [...prev];
                    for(const d of data){ if(!existingIds.has(d._id)){ merged.push(d); existingIds.add(d._id); } }
                    return merged;
                });
                skipRef.current = skipRef.current + data.length;
                setHasMore(data.length === limitRef.current);
                hasMoreRef.current = (data.length === limitRef.current);
            }
            if (!Array.isArray(data) || data.length === 0) { setHasMore(false); hasMoreRef.current = false; }
        })
        .catch(()=>{})
        .finally(()=> { loadingRef.current = false; setLoading(false); });
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // if both empty, update immediately (show message)
        if (!aboutFilter && !interestFilter) {
            setPosts([]); skipRef.current = 0; hasMoreRef.current = true; setHasMore(true); setShowFilterRequiredMessage(true);
            return;
        }
        debounceRef.current = setTimeout(() => {
            setPosts([]); skipRef.current = 0; hasMoreRef.current = true; setHasMore(true); setShowFilterRequiredMessage(false);
            // bump request id so any previous responses are ignored
            requestIdRef.current++;
            fetchPosts(0);
        }, 250);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [aboutFilter, interestFilter]);

    const applyFilters = () => {
        setPosts([]);
        skipRef.current = 0;
        requestIdRef.current++;
        fetchPosts(0);
    };

    const clearFilters = () => {
        setAboutFilter('');
        setInterestFilter('');
        setPosts([]);
        skipRef.current = 0;
        requestIdRef.current++;
        // no fetch when clearing; UI will show message
    };

    // scroll listener for lazy loading
    useEffect(() => {
        const onScroll = () => {
            if (loading) return;
            if (!hasMoreRef.current) return;
            if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
                fetchPosts(skipRef.current);
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [loading]);

    // control filter bar show/hide on scroll (mobile)
    const updateMobileStatus = () => {
        setIsMobile(window.innerWidth <= 768);
    };

    const controlFilterBar = () => {
        if (window.scrollY > lastScrollY) {
            setShowFilterBar(false);
        } else {
            setShowFilterBar(true);
        }
        setLastScrollY(window.scrollY);
    };

    useEffect(() => {
        updateMobileStatus();
        window.addEventListener('resize', updateMobileStatus);
        window.addEventListener('scroll', controlFilterBar);
        return () => {
            window.removeEventListener('resize', updateMobileStatus);
            window.removeEventListener('scroll', controlFilterBar);
        };
    }, [lastScrollY]);

    const toggleDetails = (post) => {
        if (showDetail) {
            setShowDetail(false);
            setSelectedPost(null);
        } else {
            setShowDetail(true);
            setSelectedPost(post);
        }
    };

    const changeMediaIndex = (postId, direction, mediaLength) => {
        setMediaIndices(prev => {
            const currentIdx = prev[postId] || 0;
            const newIdx = direction === 'next' 
                ? (currentIdx + 1) % mediaLength 
                : (currentIdx - 1 + mediaLength) % mediaLength;
            return { ...prev, [postId]: newIdx };
        });
    };

    return (
        <div className="search-page">
            <div className={`explore-filters ${(showFilterBar && !showDetail) ? 'show' : 'hide'}`}>
                <input className="tn-input about-filter" placeholder="Search by About Post" value={aboutFilter} onChange={e=>setAboutFilter(e.target.value)} maxLength={128} />
                <input className="tn-input interest-filter" placeholder="Search by interest" value={interestFilter} onChange={e=>setInterestFilter(e.target.value)} maxLength={80} />
                <div style={{display:'flex', gap:8}}>
                    {/* <button onClick={applyFilters} style={{flex:1,padding:'6px 10px',background:'#0084ff',color:'#fff',border:'none',borderRadius:6,fontSize:14}}>Apply</button> */}
                    {/* <button onClick={clearFilters} style={{padding:'6px 10px',background:'#eee',border:'none',borderRadius:6,fontSize:14}}>Clear</button> */}
                </div>
            </div>
            <div className="post-grid">
                {posts.length === 0 ? (
                        <div style={{padding:20,textAlign:'center',color:'#666'}}>Fill any one of the filters to see posts</div>
                    ) : posts.map((post, idx) => {
                    const hasMedia = post.media && post.media.length > 0;
                    const mediaIdx = mediaIndices[post._id] || 0;
                    let previewElement = null;
                    if (hasMedia) {
                        const m = post.media[mediaIdx];
                        if (m.type === 'image') {
                            previewElement = (
                                <img key={post._id} src={m.url} alt={post.body} className="post-item" onClick={() => toggleDetails(post)} loading="lazy" />
                            );
                        } else if (m.type === 'video') {
                            if (m.thumbnail) {
                                previewElement = (
                                    <div key={post._id} style={{position:'relative', cursor:'pointer'}} onClick={() => toggleDetails(post)}>
                                        <img src={m.thumbnail} alt={post.body} className="post-item thumbnail-img" loading="lazy" onError={(e)=>{ if (m.url) e.currentTarget.src = m.url; }} />
                                        <div className="thumbnail-overlay" aria-hidden="true">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <path d="M8 5v14l11-7L8 5z" fill="#fff" />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            } else {
                                previewElement = (
                                    <video key={post._id} src={m.url} className="post-item" muted playsInline preload="metadata" onClick={() => toggleDetails(post)} />
                                );
                            }
                        }
                    } else {
                        const src = post.photo || '';
                        previewElement = (
                            <img key={post._id} src={src} alt={post.body} className="post-item" onClick={() => toggleDetails(post)} loading="lazy" />
                        );
                    }
                    return (
                        <React.Fragment key={post._id}>
                        <div style={{position:'relative',display:'inline-block',width:'100%'}} className="post-item-container">
                            {previewElement}
                            {!showDetail && hasMedia && post.media.length > 1 && (
                                <>
                                    <button 
                                        onClick={(e) => {e.stopPropagation(); changeMediaIndex(post._id, 'prev', post.media.length);}} 
                                        style={{position:'absolute',top:'50%',left:0,transform:'translateY(-50%)',background:'rgba(0,0,0,0.3)',border:'none',color:'#fff',fontSize:'18px',cursor:'pointer',padding:'5px 8px',zIndex:5}}
                                    >
                                        ‹
                                    </button>
                                    <button 
                                        onClick={(e) => {e.stopPropagation(); changeMediaIndex(post._id, 'next', post.media.length);}} 
                                        style={{position:'absolute',top:'50%',right:0,transform:'translateY(-50%)',background:'rgba(0,0,0,0.3)',border:'none',color:'#fff',fontSize:'18px',cursor:'pointer',padding:'5px 8px',zIndex:5}}
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                        </div>

                        {/* ad thumbnail after every 3 posts */}
                        {((idx + 1) % 3 === 0) && (
                            <div style={{display:'none', width:'100%',padding:6,boxSizing:'border-box'}}>
                                        <div className="ad-thumb" style={{position:'relative',background:'#111',borderRadius:6,overflow:'hidden',cursor:'pointer'}} onClick={() => setAdOpen(true)}>
                                            <img src={AdThumbnail} alt="Sponsored" style={{width:'100%',height:160,objectFit:'cover',display:'block'}} loading="lazy" />
                                            <div style={{position:'absolute',left:'50%',top:'50%',transform:'translate(-50%,-50%)',color:'#fff',fontSize:26,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                ▶
                                            </div>
                                        </div>
                            </div>
                        )}
                        </React.Fragment>
                    );
                })}
            </div>
            {loading && (
                <div style={{display:'flex',justifyContent:'center',padding:16}} className="infinite-spinner">
                    <div className="spinner" aria-hidden="true"></div>
                </div>
            )}
            {/* Ad modal */}
            {adOpen && (
                <div role="dialog" aria-modal="true" style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000}} onClick={() => setAdOpen(false)}>
                    <div style={{width:'90%',maxWidth:720,background:'#000',borderRadius:8,overflow:'hidden',position:'relative',maxHeight:'80vh',display:'flex',flexDirection:'column'}} onClick={(e)=>e.stopPropagation()}>
                        <button aria-label="Close" onClick={() => setAdOpen(false)} style={{position:'absolute',right:12,top:12,background:'transparent',border:'none',color:'#fff',fontSize:22,cursor:'pointer',zIndex:5}}>×</button>
                        <div style={{overflowY:'auto'}}>
                            <video src={AdVideo} controls autoPlay style={{width:'100%',maxHeight:'45vh',display:'block',background:'#000',objectFit:'contain'}} />
                            <div style={{display:'flex',gap:8,padding:12,justifyContent:'center',background:'#111'}}>
                                <a href="https://www.instagram.com/bharat__music/?hl=en" target="_blank" rel="noopener noreferrer" style={{flex:1,textAlign:'center',padding:10,background:'#E1306C',color:'#fff',borderRadius:6,textDecoration:'none'}}>Instagram</a>
                                <a href="https://www.youtube.com/@bharatchandak11" target="_blank" rel="noopener noreferrer" style={{flex:1,textAlign:'center',padding:10,background:'#FF0000',color:'#fff',borderRadius:6,textDecoration:'none'}}>YouTube</a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showDetail && selectedPost && (
                <PostDetail 
                    item={selectedPost} 
                    toggleDetails={toggleDetails} 
                    hideDelete={true} 
                />
            )}
        </div>
    );
}
